import React, {Fragment, useMemo, useCallback} from 'react'

import {useTable, useSortBy, usePagination, useGlobalFilter} from "react-table";

import {Table as BTable} from "react-bootstrap" ;

import './PassportTable.scss' ;

import { getCookie, setCookie } from "root/DataFunctions/cookieFns";

// Route a cell formatter to the original react-bootstrap-table cell renderer
function formatWrapper({value, row, column})
{
   if (column.formatter !== undefined)
   {
      return column.formatter(value, row.original);
   }
   else
   {
      if (value !== undefined)
      {
         return value;
      }
      else
      {
         return '';
      }
   }
}

export const sortByChildName = (a, b, field, desc) =>
{
   const order = desc ? 'desc' : 'asc';

   if (order === 'desc')
   {
      if (a.original.child_surname > b.original.child_surname)
      {
         return -1;
      }
      else if (a.original.child_surname < b.original.child_surname)
      {
         return 1;
      }

      if (a.original.child_firstname > b.original.child_firstname)
      {
         return -1;
      }
      else if (a.original.child_firstname < b.original.child_firstname)
      {
         return 1;
      }

      return 0;
   }
   else
   {
      if (a.original.child_surname < b.original.child_surname)
      {
         return -1;
      }
      else if (a.original.child_surname > b.original.child_surname)
      {
         return 1;
      }

      if (a.original.child_firstname < b.original.child_firstname)
      {
         return -1;
      }
      else if (a.original.child_firstname > b.original.child_firstname)
      {
         return 1;
      }

      return 0;
   }
}

export const dateSorter = (a, b, field, desc) =>
{
   const dateCompareFormat = (dt) =>
   {
      if (dt === null)
      {
         return '99999999';
      }
      else
      {
         return dt.substring(6, 10) + dt.substring(3, 5) + dt.substring(0, 2);
      }
   }

   const order = desc ? 'desc' : 'asc';

   const ac = dateCompareFormat(a.values[field]);
   const bc = dateCompareFormat(b.values[field]);

   if (order === 'desc')
   {
      if (ac > bc)
      {
         return -1;
      }
      else if (ac < bc)
      {
         return 1;
      }
      return 0;
   }
   else
   {
      if (ac < bc)
      {
         return -1;
      }
      else if (ac > bc)
      {
         return 1;
      }
      return 0;
   }
}

// This function maps a react-bootstrap-table column definition to a react-table header definition, including:
// formatter:
export const xlateBootstrapTableColumns = (columns) =>
{
   const translations = columns.map((col) =>
   {
      let rval = {
         Header: col.text,
         accessor: col.dataField,
         Cell: formatWrapper
      }

      if (col.formatter !== undefined)
      {
         rval.formatter = col.formatter;
      }

      if (col.sort === undefined || col.sort === false)
      {
         rval.disableSortBy = true;
      }
      else
      {
         rval.disableSortBy = false;
      }

      if (col.width !== undefined)
      {
         rval.width = col.width;
      }

      if (col.align !== undefined)
      {
         rval.align = col.align;
      }

      if (col.headerStyle !== undefined)
      {
         rval.headerStyle = col.headerStyle;
      }

      if (col.bgColorStyle !== undefined)
      {
         rval.bgColorStyle = col.bgColorStyle;
      }

      if (col.sortType !== undefined)
      {
         rval.sortType = col.sortType;
      }

      if (col.isSorted !== undefined)
      {
         rval.isSorted = col.isSorted;
      }

      if (col.sortingFn !== undefined)
      {
         rval.sortingFn = col.sortingFn;
      }

      if (col.whiteSpace !== undefined)
      {
         rval.whiteSpace = col.whiteSpace;
      }

      return rval;
   })

   return translations;
}

const PassportTable = ({columns, data, pagination, showSearch = true, sortCookieName, userInitialState}) =>
{
   let defaultSortFields = {sortBy: []};

   if (sortCookieName !== undefined)
   {
      const cookie = getCookie(sortCookieName);
      const isSortedDesc = getCookie(sortCookieName + 'isSortedDesc');

      if (cookie !== '')
      {
         defaultSortFields = {
            sortBy: [
               {
                  id: cookie,
                  desc: isSortedDesc === 'true'
               }
            ]
         }
      }
   }

   const defaultInitialState = {...{pageIndex: 0, pageSize: pagination?.sizePerPage ?? 15}, ...userInitialState, ...defaultSortFields}

   const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      setGlobalFilter,
      rows,
      page, // Instead of using 'rows', we'll use page,
      // which has only the rows for the active page

      // The rest of these things are super handy, too ;)
      canPreviousPage,
      canNextPage,
      pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state: {pageIndex, pageSize, globalFilter},
   } = useTable(
      {
         columns,
         data,
         initialState: defaultInitialState,
         disableMultiSort: true,
         sortingFns: {
            sortNullableDate: (rowA, rowB, columnId) => {
               function compareBasic(a, b)
               {
                  return a === b ? 0 : a > b ? 1 : -1
               }

               return compareBasic(
                  (rowA.getValue(columnId))?.getTime(), // added "?." on this line
                  (rowB.getValue(columnId))?.getTime() // added "?." on this line
               )
            }
         },
      },
      useGlobalFilter,
      useSortBy,
      usePagination,
   )

   const tablePaginator = () =>
   {
      {/*
        Pagination can be built however you'd like.
        This is just a very basic UI implementation:
      */
      }

      if (pagination.hidePageListOnlyOnePage && !canNextPage && !canPreviousPage)
      {
         return <div/>
      }
      else
      {
         return (
            <div className="pagination">
               <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                  {'>>'}
               </button>
               {' '}
               <button onClick={() => nextPage()} disabled={!canNextPage}>
                  {'>'}
               </button>
               {' '}
               <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                  {'<'}
               </button>
               {' '}
               <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                  {'<<'}
               </button>
               {' '}
               <span>
          Page{' '}
                  <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>{' '}
        </span>
               <span>
          | Go to page:{' '}
                  <input
                     type="number"
                     defaultValue={pageIndex + 1}
                     onChange={e => {
                        const page = e.target.value ? Number(e.target.value) - 1 : 0
                        gotoPage(page)
                     }}
                     style={{width: '100px'}}
                  />
        </span>{' '}
               <select
                  value={pageSize}
                  onChange={e => {
                     setPageSize(Number(e.target.value))
                  }}
               >
                  {[15, 20, 30, 40, 50, 100].map(pageSize => (
                     <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                     </option>
                  ))}
               </select>
            </div>
         )

      }
   }

   // Render the UI for your table
   return (
      <div className='passport-table'>
         {showSearch === true && <div className='table-search'>
            <input
               type="text"
               value={globalFilter || ""}
               placeholder={'Search...'}
               onChange={e => setGlobalFilter(e.target.value)}
            />
         </div>}
         <BTable striped bordered hover size="sm"  {...getTableProps()}>
            <thead>
            {headerGroups.map(headerGroup => (
               <tr className='clipped-row-class' {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => {

                     const toggleProps = column.getSortByToggleProps();

                     const headerProps = column.getHeaderProps(toggleProps);

                     const clickHandler = (e) => {

                        column.toggleSortBy(!column.isSortedDesc);

                        const currentSortField = column.id;
                        const isSortedDesc = !column.isSortedDesc;

                        if (sortCookieName !== undefined)
                        {
                           setCookie(sortCookieName, currentSortField, 30);
                           setCookie(sortCookieName + 'isSortedDesc', isSortedDesc ? 'true' : 'false', 30);
                        }
                     };

                     let s1 = {} ;

                     if ( typeof(column.headerStyle) === 'function')
                     {
                        s1 = column.headerStyle({dataField: column.id}) ;
                     }
                     else
                     {
                        s1 = column.headerStyle ;
                     }

                     const style = {...s1, ...column.canSort ? {cursor: 'pointer'} : {cursor: 'default'}};

                     return (
                        <th {...headerProps} style={style} onClick={clickHandler}>
                           {column.render('Header')}
                           <span>
                                    {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                </span>
                        </th>
                     )
                  })}
               </tr>
            ))}
            </thead>
            <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
               prepareRow(row)
               return (
                  <tr className='clipped-row-class' {...row.getRowProps()}>
                     {row.cells.map(cell => {

                        let style = {};

                        if (cell.column.align !== undefined)
                        {
                           style = {textAlign: cell.column.align};
                        }

                        if (cell.column.whiteSpace !== undefined)
                        {
                           style = {...style, whiteSpace: cell.column.whiteSpace};
                        }

                        if (cell.column.bgColorStyle !== undefined)
                        {
                           style = {...style, ...cell.column.bgColorStyle(cell)};
                        }

                        return <td {...cell.getCellProps()} style={style}>{cell.render('Cell')}</td>
                     })}
                  </tr>
               )
            })}
            </tbody>
         </BTable>

         {tablePaginator()}
      </div>
   )
}

export default PassportTable;
